import React, { useCallback, useEffect, useState } from "react";
import { Mail, Send, Trash2, Eye } from "lucide-react";
import { api, apiCall } from "@/lib/api";
import { ReportFrequency, ReportSubscription } from "@/lib/types";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Spinner } from "@/components/ui/spinner";
import { toast } from "@/hooks/use-toast";
import { parseApiTimestamp } from "@/lib/datetime";

const FREQUENCY_OPTIONS: { value: ReportFrequency; label: string; hint: string }[] = [
  { value: "daily", label: "Daily", hint: "every morning, covering the previous day" },
  { value: "weekly", label: "Weekly", hint: "Monday mornings, covering Mon–Sun" },
  { value: "monthly", label: "Monthly", hint: "the 1st of each month, covering the previous month" },
];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function formatSent(iso?: string | null): string {
  if (!iso) return "Not sent yet";
  const d = parseApiTimestamp(iso);
  if (!d) return "Not sent yet";
  return `Last sent ${d.toLocaleDateString(undefined, { month: "short", day: "numeric" })}`;
}

interface Props {
  organizationId: string;
  currentUserEmail?: string | null;
}

/**
 * Who receives the emailed activity report for this organization, and how
 * often. Numbers in the report come straight from the database; there is no
 * narrative, so it is safe to send to clinic staff unattended.
 */
const ReportSubscriptionsCard: React.FC<Props> = ({ organizationId, currentUserEmail }) => {
  const [subs, setSubs] = useState<ReportSubscription[]>([]);
  const [timezone, setTimezone] = useState<string>("");
  const [loading, setLoading] = useState(true);

  const [newEmail, setNewEmail] = useState("");
  const [newFrequency, setNewFrequency] = useState<ReportFrequency>("weekly");
  const [adding, setAdding] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);

  const [testFrequency, setTestFrequency] = useState<ReportFrequency>("weekly");
  const [sendingTest, setSendingTest] = useState(false);
  const [previewHtml, setPreviewHtml] = useState<string | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const settings = await api.getReportSubscriptions(organizationId);
      setSubs(settings.subscriptions);
      setTimezone(settings.timezone);
    } catch (error) {
      console.error("Failed to load report recipients:", error);
      toast({
        title: "Could not load report recipients",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [organizationId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleAdd = async () => {
    const email = newEmail.trim().toLowerCase();
    if (!EMAIL_RE.test(email)) {
      toast({ title: "Enter a valid email address", variant: "destructive" });
      return;
    }
    setAdding(true);
    try {
      const created = await api.createReportSubscription(organizationId, {
        email,
        frequency: newFrequency,
      });
      setSubs((prev) => [...prev, created]);
      setNewEmail("");
      toast({
        title: "Recipient added",
        description: `${email} will get the ${newFrequency} report.`,
      });
    } catch (error: any) {
      const detail = error?.response?.data?.detail;
      toast({
        title: "Could not add recipient",
        description: typeof detail === "string" ? detail : undefined,
        variant: "destructive",
      });
    } finally {
      setAdding(false);
    }
  };

  const handleChange = async (
    sub: ReportSubscription,
    patch: { frequency?: ReportFrequency; is_active?: boolean }
  ) => {
    setBusyId(sub.id);
    try {
      const updated = await api.updateReportSubscription(organizationId, sub.id, patch);
      setSubs((prev) => prev.map((s) => (s.id === sub.id ? updated : s)));
    } catch (error) {
      console.error("Failed to update recipient:", error);
      toast({ title: "Could not update recipient", variant: "destructive" });
    } finally {
      setBusyId(null);
    }
  };

  const handleRemove = async (sub: ReportSubscription) => {
    if (!window.confirm(`Stop sending reports to ${sub.email}?`)) return;
    setBusyId(sub.id);
    try {
      await api.deleteReportSubscription(organizationId, sub.id);
      setSubs((prev) => prev.filter((s) => s.id !== sub.id));
    } catch (error) {
      console.error("Failed to remove recipient:", error);
      toast({ title: "Could not remove recipient", variant: "destructive" });
    } finally {
      setBusyId(null);
    }
  };

  const handleSendTest = async () => {
    setSendingTest(true);
    try {
      const res = await api.sendTestReport(organizationId, { frequency: testFrequency });
      toast({
        title: "Test report sent",
        description: `Sent the ${testFrequency} report for ${res.period} to ${res.sent_to}.`,
      });
    } catch (error: any) {
      const detail = error?.response?.data?.detail;
      toast({
        title: "Could not send test report",
        description: typeof detail === "string" ? detail : undefined,
        variant: "destructive",
      });
    } finally {
      setSendingTest(false);
    }
  };

  const handlePreview = async () => {
    setLoadingPreview(true);
    try {
      const html = await apiCall<string>({
        url: api.reportPreviewUrl(organizationId, testFrequency),
        method: "GET",
        responseType: "text",
      });
      setPreviewHtml(html);
    } catch (error) {
      console.error("Failed to load preview:", error);
      toast({ title: "Could not load preview", variant: "destructive" });
    } finally {
      setLoadingPreview(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Mail className="h-4 w-4" />
          Emailed activity reports
        </CardTitle>
        <CardDescription>
          A summary of calls, texts, consents, check-ins and action items, computed
          directly from the database and emailed on a schedule. Days, weeks and months
          are counted in {timezone || "the organization's time zone"}.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {loading ? (
          <div className="flex items-center gap-2 text-muted-foreground">
            <Spinner size="small" /> Loading recipients…
          </div>
        ) : (
          <>
            {subs.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                No one receives this report yet. Add an address below.
              </p>
            ) : (
              <div className="overflow-x-auto">
              <Table className="min-w-[640px]">
                <TableHeader>
                  <TableRow>
                    <TableHead>Recipient</TableHead>
                    <TableHead className="w-[160px]">Cadence</TableHead>
                    <TableHead className="w-[120px]">Status</TableHead>
                    <TableHead className="w-[150px]">Delivery</TableHead>
                    <TableHead className="w-[60px]" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {subs.map((sub) => (
                    <TableRow key={sub.id} className={sub.is_active ? "" : "opacity-60"}>
                      <TableCell className="font-medium break-all">{sub.email}</TableCell>
                      <TableCell>
                        <Select
                          value={sub.frequency}
                          disabled={busyId === sub.id}
                          onValueChange={(v) => handleChange(sub, { frequency: v as ReportFrequency })}
                        >
                          <SelectTrigger className="h-8">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {FREQUENCY_OPTIONS.map((o) => (
                              <SelectItem key={o.value} value={o.value}>
                                {o.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="sm"
                          disabled={busyId === sub.id}
                          onClick={() => handleChange(sub, { is_active: !sub.is_active })}
                        >
                          {sub.is_active ? "Active" : "Paused"}
                        </Button>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {formatSent(sub.last_sent_at)}
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="icon"
                          aria-label={`Remove ${sub.email}`}
                          disabled={busyId === sub.id}
                          onClick={() => handleRemove(sub)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              </div>
            )}

            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              <Input
                type="email"
                placeholder="name@clinic.com"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleAdd()}
                className="sm:max-w-xs"
              />
              <Select value={newFrequency} onValueChange={(v) => setNewFrequency(v as ReportFrequency)}>
                <SelectTrigger className="sm:w-[140px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {FREQUENCY_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button onClick={handleAdd} disabled={adding || !newEmail.trim()}>
                {adding ? <Spinner className="mr-2" size="small" /> : null}
                Add recipient
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              {FREQUENCY_OPTIONS.map((o) => `${o.label}: ${o.hint}`).join(" · ")}
            </p>

            <div className="border-t pt-4 flex flex-col gap-2 sm:flex-row sm:items-center">
              <span className="text-sm text-muted-foreground">See what recipients get:</span>
              <Select value={testFrequency} onValueChange={(v) => setTestFrequency(v as ReportFrequency)}>
                <SelectTrigger className="sm:w-[140px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {FREQUENCY_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button variant="outline" onClick={handlePreview} disabled={loadingPreview}>
                {loadingPreview ? <Spinner className="mr-2" size="small" /> : <Eye className="mr-2 h-4 w-4" />}
                Preview
              </Button>
              <Button variant="outline" onClick={handleSendTest} disabled={sendingTest}>
                {sendingTest ? <Spinner className="mr-2" size="small" /> : <Send className="mr-2 h-4 w-4" />}
                Email me a copy{currentUserEmail ? ` (${currentUserEmail})` : ""}
              </Button>
            </div>
          </>
        )}
      </CardContent>

      <Dialog open={previewHtml !== null} onOpenChange={(open) => !open && setPreviewHtml(null)}>
        <DialogContent className="max-w-4xl h-[85vh] p-0 overflow-hidden">
          <DialogHeader className="px-6 pt-6 pb-2">
            <DialogTitle>Report preview</DialogTitle>
          </DialogHeader>
          {previewHtml !== null && (
            <iframe
              title="Report preview"
              srcDoc={previewHtml}
              sandbox=""
              className="w-full h-full border-0 bg-[#F7F6F2]"
            />
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default ReportSubscriptionsCard;
